// MUI-free copy of Ui-components/ai-stack-table.
import {memo} from 'react';
import type {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import styles from './AiStackTable.module.scss';

export interface TechStackItem {
    label: string;
    icon?: string | StaticImageData;
}

export interface TechStackRow {
    category: string;
    items: TechStackItem[];
}

interface AiStackTableProps {
    heading: string;
    subheading?: string;
    contentData: TechStackRow[];
    /** Set when the section sits on a dark background. */
    dark?: boolean;
    /** Heading block alignment; `left` for pages whose sections lead with a left-aligned header. */
    align?: 'center' | 'left';
    /** Heading steps down on small screens (28 → 32 → 36 → 48) instead of 35 → 48. */
    compactHeading?: boolean;
    /** From lg, let the heading run the full container width (one line) instead of the standard 1024px. */
    wideHeading?: boolean;
}

const AiStackTable = ({
    heading,
    subheading,
    contentData,
    dark = false,
    align = 'center',
    compactHeading = false,
    wideHeading = false
}: AiStackTableProps) => (
    <div>
        <Container>
            <div
                className={[styles.header, align === 'left' && styles.headerLeft, wideHeading && styles.headerWide]
                    .filter(Boolean)
                    .join(' ')}
            >
                <h2
                    className={['font-headers', styles.heading, compactHeading && styles.headingCompact, dark && styles.onDark]
                        .filter(Boolean)
                        .join(' ')}
                >
                    {heading ? parse(heading) : ''}
                </h2>
                {subheading && (
                    <p
                        className={
                            dark
                                ? `font-regular-text ${styles.subheading} ${styles.subheadingDark}`
                                : `font-regular-text ${styles.subheading}`
                        }
                    >
                        {parse(subheading)}
                    </p>
                )}
            </div>
            <div>
                {contentData?.map((row) => (
                    <div key={row.category} className={styles.rowCard}>
                        <div className={styles.categoryLabel}>
                            <p className={`font-headers ${styles.categoryText}`}>{row.category}</p>
                        </div>
                        <div className={styles.chips}>
                            {row.items.map((item) => (
                                <div key={item.label} className={styles.chip}>
                                    {item.icon && (
                                        <div className={styles.chipIcon}>
                                            <OptimizedImage src={item.icon} alt={item.label} width={20} height={20} />
                                        </div>
                                    )}
                                    <p className={styles.chipLabel}>{item.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </Container>
    </div>
);

export default memo(AiStackTable);
